/**
 * Shared Schemas
 *
 * This file contains common Zod schemas that represent core entities in our domain model
 * which are used across multiple services and API endpoints.
 *
 * ISOLATION CONSTRAINT (KTD4): never add `export * from './theme-derive'` (or
 * any re-export of it) here. `./theme-derive` is the one module that imports
 * `@material/material-color-utilities`; this barrel is re-exported from the
 * root `@school-journey/shared-types` package that mobile also imports, so
 * doing so would leak MCU's image-quantization module into the mobile
 * bundle. `theme-derive` is reachable ONLY via its own subpath export.
 */

import { z } from 'zod'

// Re-export all entity schemas (includes navigation entities:
// NavigationTypeSchema, UserRoleSchema, NavigationItemSchema,
// TileGridConfigSchema, TileItemSchema, etc.)
export * from './entities'

// Notifications (push payload contract)
export * from './notifications'

// Local imports of navigation schemas needed to compose NavigationStructureSchema below.
import { NavigationItemSchema, TileGridConfigSchema, TileItemSchema } from './entities/navigation'

/**
 * School Schema - Simple representation for backwards compatibility
 * @deprecated Use SchoolSummarySchema or SchoolListItemSchema from './entities/school'
 */
export const SchoolSchema = z.object({
  id: z.string().describe('School public ID'),
  name: z.string().describe('School name')
})

export type School = z.infer<typeof SchoolSchema>

/**
 * Author Schema - Core representation of an author/user
 * Used for content attribution across the platform
 */
export const AuthorSchema = z
  .object({
    id: z.string().nullable().describe('Author public ID'),
    name: z.string().describe('Author name')
  })
  .nullable()

export type Author = z.infer<typeof AuthorSchema>

/**
 * Navigation Structure Schema - Complete navigation menu structure
 * Used for both request and response schemas across navigation endpoints
 */
export const NavigationStructureSchema = z.object({
  navigation: z.object({
    drawer: z.object({
      items: z.array(NavigationItemSchema)
    }),
    tabs: z.object({
      items: z.array(NavigationItemSchema)
    }),
    /**
     * @deprecated Use `homeWidget` instead. The `tiles` key is preserved as a
     * readable legacy slot. Full-screen tile authoring moved to `Page` rows
     * (template = TILE_SCREEN); the standalone `tileScreen` slot was retired.
     */
    tiles: z
      .object({
        config: TileGridConfigSchema.optional(),
        items: z.array(TileItemSchema)
      })
      .optional(),
    homeWidget: z
      .object({
        config: TileGridConfigSchema.optional(),
        items: z.array(TileItemSchema)
      })
      .optional()
  })
})

export type NavigationStructure = z.infer<typeof NavigationStructureSchema>
